import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { GlyphWind } from "./glyphWind";

export type GlyphFlowPointer = {
  pointerId: number;
  x: number;
  y: number;
  pointerType?: string;
};

export type GlyphFlowHandle = {
  pointerDown: (pointer: GlyphFlowPointer) => void;
  pointerMove: (pointer: GlyphFlowPointer) => void;
  pointerUp: (pointer: GlyphFlowPointer) => boolean;
  pointerCancel: (pointerId?: number) => void;
};

type GlyphFallbackController = {
  impulse: (x: number, y: number) => void;
  wake: (x: number, y: number) => void;
};

type Props = {
  onMotionChange?: (paused: boolean) => void;
  userPaused?: boolean;
  reducedMotion?: boolean;
};

type ActivePointer = GlyphFlowPointer & { moved: boolean };

const POINTER_DRAG_THRESHOLD = 8;

function useReducedMotion(controlled?: boolean) {
  const [reduced, setReduced] = useState(() =>
    controlled ?? (typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches),
  );

  useEffect(() => {
    if (controlled !== undefined) return;
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [controlled]);

  return controlled ?? reduced;
}

/**
 * Reuses the approved Glyph Flow visual language whenever WebGL2 is available.
 * Input remains deliberately outside this component: HomePage owns the wheel
 * pointer capture and hands only confirmed free-space taps to this background.
 */
export const GlyphFlowBackground = forwardRef<GlyphFlowHandle, Props>(function GlyphFlowBackground(
  { onMotionChange, userPaused = false, reducedMotion: controlledReducedMotion },
  ref,
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<ReturnType<typeof GlyphWind.create> | null>(null);
  const fallbackRef = useRef<GlyphFallbackController | null>(null);
  const pointerRef = useRef<ActivePointer | null>(null);
  const [fallback, setFallback] = useState(false);
  const reducedMotion = useReducedMotion(controlledReducedMotion);
  const [documentVisible, setDocumentVisible] = useState(() =>
    typeof document === "undefined" || !document.hidden,
  );
  const paused = userPaused || reducedMotion || !documentVisible;
  const pausedRef = useRef(paused);
  const reducedRef = useRef(reducedMotion);
  const visibleRef = useRef(documentVisible);

  useEffect(() => {
    pausedRef.current = paused;
    if (paused) pointerRef.current = null;
    reducedRef.current = reducedMotion;
    visibleRef.current = documentVisible;
    const renderer = rendererRef.current;
    if (renderer) {
      renderer.setReducedMotion(reducedMotion);
      renderer.setDocumentVisible(documentVisible);
      renderer.setRunning(!paused);
    }
  }, [documentVisible, paused, reducedMotion]);

  useEffect(() => {
    onMotionChange?.(paused);
  }, [onMotionChange, paused]);

  useEffect(() => {
    const onVisibilityChange = () => setDocumentVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onVisibilityChange);
    window.addEventListener("focus", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onVisibilityChange);
      window.removeEventListener("focus", onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (fallback || !hostRef.current) return;
    let active = true;
    const renderer = GlyphWind.create({
      element: hostRef.current,
      settings: () => {
        const settings = GlyphWind.defaults();
        settings.look = "mixed";
        settings.strength.rest = 0;
        settings.wind.revealS = 1;
        settings.grid.cellCss = 7.8;
        settings.grid.mobileCellCss = 9;
        // Freigegebene Refined-Grenzen: keine ungebremste Raster- oder DPR-Größe.
        settings.grid.maxCells = 20000;
        settings.grid.mobileMaxCells = 4000;
        settings.grid.maxDevicePixels = 4000000;
        return settings;
      },
      timing: () => ({ fadeIn: 700 }),
      onReady: () => {
        if (!active) return;
        renderer.setReducedMotion(reducedRef.current);
        renderer.setDocumentVisible(visibleRef.current);
        renderer.setRunning(!pausedRef.current);
      },
      onError: () => {
        if (active) setFallback(true);
      },
      onContextLost: () => {
        if (active) setFallback(true);
      },
    });
    rendererRef.current = renderer;

    return () => {
      active = false;
      pointerRef.current = null;
      if (rendererRef.current === renderer) rendererRef.current = null;
      renderer.destroy();
    };
  }, [fallback]);

  useImperativeHandle(ref, () => ({
    pointerDown(pointer) {
      if (pausedRef.current) return;
      pointerRef.current = { ...pointer, moved: false };
    },
    pointerMove(pointer) {
      const activePointer = pointerRef.current;
      if (activePointer) {
        if (activePointer.pointerId !== pointer.pointerId) return;
        if (Math.hypot(pointer.x - activePointer.x, pointer.y - activePointer.y) > POINTER_DRAG_THRESHOLD) {
          activePointer.moved = true;
        }
        return;
      }
      if (!pausedRef.current && pointer.pointerType === "mouse") {
        rendererRef.current?.wakeInput(pointer.x, pointer.y);
        fallbackRef.current?.wake(pointer.x, pointer.y);
      }
    },
    pointerUp(pointer) {
      const activePointer = pointerRef.current;
      if (!activePointer || activePointer.pointerId !== pointer.pointerId) return false;
      pointerRef.current = null;
      if (pausedRef.current || activePointer.moved) return false;
      const rendererHandled = rendererRef.current?.impulse(activePointer.x, activePointer.y) ?? false;
      if (!rendererHandled) fallbackRef.current?.impulse(activePointer.x, activePointer.y);
      return true;
    },
    pointerCancel(pointerId) {
      if (pointerId == null || pointerRef.current?.pointerId === pointerId) pointerRef.current = null;
    },
  }), []);

  return (
    <div
      ref={hostRef}
      className="glyph-flow-background"
      aria-hidden="true"
      data-renderer={fallback ? "canvas2d-fallback" : "webgl2"}
      data-motion={paused ? "paused" : "running"}
    >
      {fallback ? (
        <GlyphCanvasFallback controllerRef={fallbackRef} paused={paused} />
      ) : null}
    </div>
  );
});

function GlyphCanvasFallback({
  controllerRef,
  paused,
}: {
  controllerRef: MutableRefObject<GlyphFallbackController | null>;
  paused: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pausedRef = useRef(paused);
  const playbackRef = useRef<{ start: () => void; stop: () => void } | null>(null);

  useEffect(() => {
    pausedRef.current = paused;
    if (paused) playbackRef.current?.stop();
    else playbackRef.current?.start();
  }, [paused]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) return;

    let frame = 0;
    let destroyed = false;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let time = 0;
    let lastFrame = 0;
    const ripples: Array<{ x: number; y: number; at: number; shape: number }> = [];
    const cells: Array<{ x: number; y: number; phase: number; glyph: string; alpha: number }> = [];
    const glyphs = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789{}<>[]/+=·";

    const resize = () => {
      const box = canvas.getBoundingClientRect();
      width = Math.max(1, Math.round(box.width));
      height = Math.max(1, Math.round(box.height));
      dpr = Math.min(window.devicePixelRatio || 1, 1.5, Math.sqrt(4000000 / Math.max(1, width * height)));
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      cells.length = 0;
      const mobile = width <= 767;
      const maxCells = mobile ? 900 : 3600;
      const step = mobile ? 16 : 12;
      const columns = Math.max(1, Math.floor(width / step));
      const rows = Math.max(1, Math.floor(height / (step * 1.6)));
      const stride = Math.max(1, Math.ceil(Math.sqrt((columns * rows) / maxCells)));
      for (let row = 0; row < rows; row += stride) {
        for (let column = 0; column < columns; column += stride) {
          const seed = Math.sin((row + 17) * 71.7 + (column + 11) * 13.3) * 10000;
          const random = seed - Math.floor(seed);
          cells.push({
            x: (column + 0.5) * step,
            y: (row + 0.5) * step * 1.6,
            phase: random * Math.PI * 2,
            glyph: glyphs[Math.floor(random * glyphs.length)] ?? "·",
            alpha: 0.08 + random * 0.16,
          });
        }
      }
      draw(time);
    };

    const draw = (now: number) => {
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);
      context.font = "9px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
      context.textAlign = "center";
      context.textBaseline = "middle";
      const calmRadius = Math.min(220, Math.max(160, Math.min(width, height) * 0.23));
      const liveRipples = ripples.filter((ripple) => now - ripple.at < 3800);
      ripples.splice(0, ripples.length, ...liveRipples);
      for (const cell of cells) {
        const drift = pausedRef.current ? 0 : now * 0.00018;
        let x = cell.x + Math.sin(drift + cell.phase) * 3;
        let y = cell.y + Math.cos(drift * 0.8 + cell.phase) * 2;
        let boost = 0;
        for (const ripple of liveRipples) {
          const age = now - ripple.at;
          const radius = 40 + age * 0.18;
          const distance = Math.hypot(x - ripple.x, y - ripple.y);
          boost += Math.max(0, 1 - Math.abs(distance - radius) / 68) * Math.sin(Math.min(Math.PI, age / 3800 * Math.PI)) * 0.44;
        }
        const centreDistance = Math.hypot(x - width / 2, y - height / 2);
        const calm = Math.min(1, Math.max(0.22, (centreDistance - calmRadius * 0.7) / calmRadius));
        const cloud = 0.4 + 0.6 * Math.max(0, Math.sin(x * 0.012 + y * 0.008 + drift));
        const alpha = (cell.alpha * cloud + boost) * calm;
        if (alpha < 0.035) continue;
        context.fillStyle = `rgba(101, 185, 204, ${Math.min(0.7, alpha)})`;
        context.fillText(cell.glyph, x, y);
      }
    };

    const tick = (now: number) => {
      frame = 0;
      if (destroyed || pausedRef.current || document.hidden) return;
      if (!lastFrame) lastFrame = now;
      if (now - lastFrame >= 33) {
        time += Math.min(now - lastFrame, 150);
        lastFrame = now;
        draw(time);
      }
      frame = requestAnimationFrame(tick);
    };

    const start = () => {
      if (!destroyed && !pausedRef.current && !document.hidden && !frame) {
        lastFrame = performance.now();
        frame = requestAnimationFrame(tick);
      }
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    playbackRef.current = {
      start,
      stop() {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        lastFrame = 0;
      },
    };
    controllerRef.current = {
      impulse(x, y) {
        ripples.push({ x, y, at: time, shape: Math.random() });
        if (ripples.length > 6) ripples.shift();
        draw(time);
        start();
      },
      wake() {
        // The fallback stays intentionally calm; only confirmed taps create a visible impulse.
      },
    };
    resize();
    start();

    return () => {
      destroyed = true;
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      controllerRef.current = null;
      playbackRef.current = null;
    };
  }, [controllerRef]);

  return <canvas ref={canvasRef} className="glyph-flow-fallback" aria-hidden="true" />;
}
